import { getHttpClient } from './httpClient';

// ============================================================================
// Types
// ============================================================================

export interface Enterprise {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  settings: Record<string, unknown>;
  plan_slug?: string | null;
  plan_name?: string | null;
  subscription_status?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlanTier {
  id: string;
  slug: string;
  name: string;
  price_monthly_cents: number;
  pricing_model: string;
  is_public: boolean;
  /**
   * Nombre minimum de sièges achetables pour ce plan (cf migration 099).
   * - solo : 1
   * - detect / solve / lead : 5
   * - beta : 1 (par défaut, hors contrainte commerciale)
   * Servir comme min de l'input + validation côté backend.
   */
  min_seats: number;
}

export interface Team {
  id: string;
  enterprise_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  is_root: boolean;
  depth: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Enterprises
// ============================================================================

export async function listEnterprises(): Promise<Enterprise[]> {
  const response = await getHttpClient().get<Enterprise[]>('/api/admin/enterprises');
  return response.data;
}

export async function getEnterprise(id: string): Promise<Enterprise> {
  const response = await getHttpClient().get<Enterprise>(`/api/admin/enterprises/${id}`);
  return response.data;
}

export async function createEnterprise(data: { name: string; slug?: string; plan_slug?: string }): Promise<Enterprise> {
  const response = await getHttpClient().post<Enterprise>('/api/admin/enterprises', data);
  return response.data;
}

export async function updateEnterprise(id: string, data: { name?: string; slug?: string; plan_slug?: string }): Promise<Enterprise> {
  const response = await getHttpClient().put<{ ok: boolean; enterprise: Enterprise }>(`/api/admin/enterprises/${id}`, data);
  return response.data.enterprise;
}

export async function listPlans(): Promise<PlanTier[]> {
  const response = await getHttpClient().get<PlanTier[]>('/api/admin/plans');
  return response.data;
}

export async function deleteEnterprise(id: string): Promise<void> {
  await getHttpClient().delete(`/api/admin/enterprises/${id}`);
}

export async function purgeEnterprise(id: string): Promise<{ message: string }> {
  const { data } = await getHttpClient().delete(`/api/admin/enterprises/${id}/purge`);
  return data;
}

// ============================================================================
// Teams
// ============================================================================

export async function listTeams(enterpriseId: string): Promise<Team[]> {
  const response = await getHttpClient().get<Team[]>(`/api/admin/teams?enterprise_id=${enterpriseId}`);
  return response.data;
}

export async function createTeam(data: { enterprise_id: string; parent_id?: string | null; name: string; description?: string }): Promise<Team> {
  const response = await getHttpClient().post<Team>('/api/admin/teams', data);
  return response.data;
}

export async function updateTeam(id: string, data: { name?: string; description?: string }): Promise<Team> {
  const response = await getHttpClient().put<{ ok: boolean; team: Team }>(`/api/admin/teams/${id}`, data);
  return response.data.team;
}

export async function deleteTeam(id: string): Promise<void> {
  await getHttpClient().delete(`/api/admin/teams/${id}`);
}
